import React, { useEffect, useState } from 'react';

// Brand-specific logo overrides - for brands whose published logo is white-on-transparent,
// has a stale CDN URL, or where the domain registered in the CRM doesn't resolve to a
// usable favicon. Keys are matched against the brand name (after lowercasing and
// stripping non-alphanumerics). The first override hit produces candidates that are
// PREPENDED to whatever the caller passes - so if every override 404s we still fall
// through to the caller's website-derived candidates and finally to the initials.
//
// NOTE (Feb 2026): Clearbit shut down `logo.clearbit.com` - the domain no
// longer resolves (`ERR_NAME_NOT_RESOLVED`). Every Clearbit URL has been
// removed. Preferred order is now: brand's own site → Wikipedia SVG →
// Google favicons (s2/favicons) → DuckDuckGo (icons.duckduckgo.com).
const BRAND_LOGO_OVERRIDES = [
  {
    match: 'weyan',
    candidates: [
      'https://www.weyan.app/favicon.png',
      'https://www.weyan.app/logos/NavLogo.svg',
      'https://icons.duckduckgo.com/ip3/weyan.app.ico',
    ],
  },
  {
    // Both "Coca-Cola Nigeria Limited" (seed) and "Coca Cola" (workbook).
    match: 'cocacola',
    candidates: [
      'https://upload.wikimedia.org/wikipedia/commons/c/ce/Coca-Cola_logo.svg',
      'https://www.google.com/s2/favicons?sz=256&domain=coca-cola.com',
      'https://icons.duckduckgo.com/ip3/coca-cola.com.ico',
    ],
  },
  {
    match: 'mtn',
    candidates: [
      'https://upload.wikimedia.org/wikipedia/commons/9/93/New-mtn-logo.jpg',
      'https://www.google.com/s2/favicons?sz=256&domain=mtn.com',
      'https://icons.duckduckgo.com/ip3/mtn.com.ico',
    ],
  },
  {
    // "Nigerian Breweries PLC (Star Lager)" - parent brand Heineken.
    match: 'nigerianbreweries',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=nbplc.com',
      'https://icons.duckduckgo.com/ip3/nbplc.com.ico',
      'https://www.google.com/s2/favicons?sz=256&domain=heineken.com',
    ],
  },
  {
    match: 'starlager',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=nbplc.com',
      'https://icons.duckduckgo.com/ip3/nbplc.com.ico',
    ],
  },
  {
    match: 'heineken',
    candidates: [
      'https://upload.wikimedia.org/wikipedia/commons/e/ea/Heineken_logo.svg',
      'https://www.google.com/s2/favicons?sz=256&domain=heineken.com',
      'https://icons.duckduckgo.com/ip3/heineken.com.ico',
    ],
  },
  {
    match: 'pernodricard',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=pernod-ricard.com',
      'https://icons.duckduckgo.com/ip3/pernod-ricard.com.ico',
    ],
  },
  {
    // "OSF", "Open Society Foundation(s)", "Open Society Initiatives for West Africa"
    match: 'opensociet',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=opensocietyfoundations.org',
      'https://icons.duckduckgo.com/ip3/opensocietyfoundations.org.ico',
    ],
  },
  {
    match: 'osiwa',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=osiwa.org',
      'https://icons.duckduckgo.com/ip3/osiwa.org.ico',
    ],
  },
  {
    match: 'cjid',
    candidates: [
      'https://thecjid.org/wp-content/uploads/2021/08/dark-cr-logo-dsk.svg',
      'https://www.google.com/s2/favicons?sz=256&domain=thecjid.org',
      'https://icons.duckduckgo.com/ip3/thecjid.org.ico',
    ],
  },
  {
    match: 'donjulio',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=donjulio.com',
      'https://icons.duckduckgo.com/ip3/donjulio.com.ico',
    ],
  },
  {
    match: 'pulsepeak',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=pulsepeak.co',
      'https://icons.duckduckgo.com/ip3/pulsepeak.co.ico',
    ],
  },
  {
    match: 'testbrand',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=thcohq.com',
      'https://icons.duckduckgo.com/ip3/thcohq.com.ico',
    ],
  },
  {
    match: 'houseofstacy',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=houseofstacy.com',
      'https://icons.duckduckgo.com/ip3/houseofstacy.com.ico',
      'https://www.google.com/s2/favicons?sz=256&domain=shopfrom23.com',
      'https://icons.duckduckgo.com/ip3/shopfrom23.com.ico',
    ],
  },
  {
    // The bare "OSF" acronym (key === 'osf') isn't caught by the 'opensociet'
    // pattern above. Place this last so longer patterns still win.
    match: 'osf',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=opensocietyfoundations.org',
      'https://icons.duckduckgo.com/ip3/opensocietyfoundations.org.ico',
    ],
  },
  {
    match: 'allsmiles',
    candidates: [
      'https://www.google.com/s2/favicons?sz=256&domain=allsmiles.com.ng',
      'https://icons.duckduckgo.com/ip3/allsmiles.com.ng.ico',
    ],
  },
];

const normaliseBrandKey = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const overrideCandidatesFor = (name) => {
  const key = normaliseBrandKey(name);
  if (!key) return [];
  const hit = BRAND_LOGO_OVERRIDES.find((entry) => key.includes(entry.match));
  return hit ? hit.candidates : [];
};

export { overrideCandidatesFor };

// Sites that host pages ABOUT brands - pins, galleries, press, directories,
// marketplaces, socials. A brand whose website field points at one of these
// (e.g. a Pinterest pin it was found on) must not borrow that site's favicon
// as its logo. Mirrors THIRD_PARTY_DOMAINS in backend/official_brand_logo.py.
const THIRD_PARTY_LOGO_DOMAINS = [
  'pinterest.com', 'pinterest.co.uk', 'pinterest.ca', 'pinterest.fr', 'pinterest.de', 'pinterest.it',
  'pinterest.es', 'pinterest.com.au', 'pin.it', 'pinimg.com', 'tumblr.com', 'reddit.com', 'quora.com',
  'flickr.com', 'behance.net', 'dribbble.com', 'imgur.com', 'giphy.com', 'threads.net', 'fandom.com',
  'blogspot.com', 'wikimedia.org', 'wikipedia.org', 'brandsoftheworld.com', 'seeklogo.com',
  'logos-world.net', '1000logos.net', 'worldvectorlogo.com', 'freepik.com', 'shutterstock.com',
  'gettyimages.com', 'istockphoto.com', 'pngwing.com', 'pngtree.com', 'instagram.com', 'facebook.com',
  'x.com', 'twitter.com', 'tiktok.com', 'linkedin.com', 'youtube.com', 'apps.apple.com',
  'play.google.com', 'crunchbase.com', 'amazon.com', 'etsy.com', 'ebay.com', 'jumia.com.ng', 'jiji.ng',
  'konga.com', 'linktr.ee', 'medium.com', 'forbes.com', 'bloomberg.com', 'reuters.com', 'bbc.com',
  'cnn.com', 'vogue.com',
];

// A contact's email domain names the brand's site only when it carries the
// brand's name (sam@gucci.com for Gucci) - an agency or personal domain
// (dean@thcohqs.com) is someone else's. Mirrors _email_domain_matches_brand
// in backend/v3_routes.py.
const NAME_NOISE = new Set(['ltd', 'limited', 'inc', 'incorporated', 'co', 'company', 'corp', 'corporation',
  'group', 'global', 'intl', 'international', 'nigeria', 'ng', 'africa', 'plc', 'holdings', 'the', 'and']);
export const emailDomainMatchesBrand = (domain, brandName) => {
  const label = String(domain || '').toLowerCase().split('.')[0].replace(/[^a-z0-9]/g, '');
  const key = String(brandName || '').toLowerCase().split(/[^a-z0-9]+/).filter((t) => t && !NAME_NOISE.has(t)).join('');
  return label.length >= 3 && key.length >= 3 && (label.includes(key) || key.includes(label));
};

export const isThirdPartyLogoDomain = (domain) => {
  const value = String(domain || '').toLowerCase().replace(/^www\./, '');
  return Boolean(value) && THIRD_PARTY_LOGO_DOMAINS.some((d) => value === d || value.endsWith(`.${d}`));
};

// Kept for backwards compatibility with imports elsewhere in the codebase.
export const WEYAN_LOGO_URL = 'https://www.weyan.app/favicon.png';
export const isWeYanBrand = (name) => normaliseBrandKey(name).includes('weyan');

const CACHE_KEY = 'tasck_brand_logo_cache';
const CACHE_VERSION = 3; // bumped: v2 caches held dead logo.clearbit.com URLs

// Preseeded cache. On module load we merge this into localStorage so cold
// visits render every known brand's logo instantly - no sequential HEAD
// probing through Clearbit/Google/DuckDuckGo before the first working URL is
// found. Users on a fresh device or after clearing localStorage still get an
// immediate render.
const SEED_LOGO_CACHE = BRAND_LOGO_OVERRIDES.reduce((acc, entry) => {
  if (entry?.match && entry?.candidates?.length) {
    acc[entry.match] = entry.candidates[0];
  }
  return acc;
}, {});

const readCache = () => {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CACHE_VERSION) return {};
    return parsed.entries || {};
  } catch (_) {
    return {};
  }
};

const writeCache = (entries) => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ version: CACHE_VERSION, entries }));
  } catch (_) {
    // Storage full or unavailable; ignore and keep rendering.
  }
};

// Merge SEED_LOGO_CACHE into localStorage once per session - user-cached
// entries always win over the seed (they were verified by an actual onLoad).
// Runs at module import time so BrandLogo's first render already sees a hit.
(() => {
  if (typeof window === 'undefined') return;
  try {
    const existing = readCache();
    let changed = false;
    Object.entries(SEED_LOGO_CACHE).forEach(([key, url]) => {
      if (!existing[key]) {
        existing[key] = url;
        changed = true;
      }
    });
    if (changed) writeCache(existing);
    // Preload every seeded URL into the browser HTTP cache so the eventual
    // <img> render is served from cache in milliseconds rather than a fresh
    // network round-trip. Fire-and-forget: errors are ignored.
    if (typeof Image !== 'undefined') {
      Object.values(existing).forEach((url) => {
        if (!url) return;
        try { const img = new Image(); img.src = url; } catch (_) { /* noop */ }
      });
    }
  } catch (_) {
    // localStorage / Image unavailable - skip warm-up, fall back to on-demand.
  }
})();

const brandCacheKey = (name) => normaliseBrandKey(name);

export const getCachedBrandLogo = (name) => {
  if (!name) return '';
  const key = brandCacheKey(name);
  const entries = readCache();
  // Exact match wins - this is a URL previously verified by an onLoad
  // handler for this exact brand.
  if (entries[key]) return entries[key];
  // Fall back to the seed cache pattern (e.g. brand key "cocacolanigeria"
  // matches seed key "cocacola") so cold visits still get an instant URL
  // without probing the full candidate chain.
  const seedHit = Object.entries(SEED_LOGO_CACHE).find(([seedKey]) => seedKey && key.includes(seedKey));
  return seedHit ? seedHit[1] : '';
};

export const setCachedBrandLogo = (name, url) => {
  if (!name || !url) return;
  const entries = readCache();
  entries[brandCacheKey(name)] = url;
  writeCache(entries);
};

/**
 * True for a URL long enough to be junk rather than a link.
 *
 * An uploaded logo is stored inline as a `data:` URI and is legitimately
 * thousands of characters long, so the length test must not apply to it -
 * applying it dropped every uploaded logo from the candidate list, which is
 * why an upload saved correctly and then never appeared.
 */
export const truncate = (url, max = 512) => {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (trimmed.slice(0, 5).toLowerCase() === 'data:') return false;
  return trimmed.length > max;
};

/**
 * An inline logo - an upload held on the record as a `data:` URI.
 *
 * Not the same question as "is this stored on the brand": a scraped logo is
 * saved as an ordinary https URL and is just as explicit. Callers say which
 * candidate came from the record by passing it as `storedLogo`; this helper
 * only catches inline ones that arrive inside a candidate list.
 */
export const isInlineLogo = (url) => typeof url === 'string' && /^(data:|blob:)/i.test(url.trim());

const deriveInitials = (name = '') => {
  const clean = String(name).trim();
  if (!clean) return 'BR';
  if (clean.length <= 3) return clean.toUpperCase();
  return clean.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'BR';
};

const DARK_CACHE_KEY = 'tasck_brand_logo_dark_cache';

const readDarkCache = () => {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(DARK_CACHE_KEY);
    return raw ? (JSON.parse(raw) || {}) : {};
  } catch (_) {
    return {};
  }
};

const writeDarkCache = (entries) => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DARK_CACHE_KEY, JSON.stringify(entries));
  } catch (_) { /* noop */ }
};

// Best-effort detection of a "white" logo (white marks on a transparent/white field)
// so the tile can switch to a dark background and keep the logo visible.
// Uses a separate CORS-enabled image so the visible <img> (which is NOT CORS-bound and
// therefore always loads) is never affected. If the host blocks canvas reads, we simply
// can't inspect it and leave the tile as-is.
const detectWhiteLogo = (url, cb) => {
  if (typeof window === 'undefined' || !url) return;
  // Cached decisions (dark true/false, or 0 for "CORS blocked, don't retry")
  // short-circuit the probe so we never re-fetch the same logo just to check.
  const cache = readDarkCache();
  if (Object.prototype.hasOwnProperty.call(cache, url)) {
    if (cache[url] === true) cb(true);
    return;
  }
  const probe = new window.Image();
  probe.crossOrigin = 'anonymous';
  const markProbed = (isDark) => {
    const next = readDarkCache();
    next[url] = isDark;
    writeDarkCache(next);
  };
  probe.onerror = () => markProbed(0); // CORS blocked or 404 - never retry.
  probe.onload = () => {
    try {
      const w = Math.min(probe.naturalWidth || 48, 48);
      const h = Math.min(probe.naturalHeight || 48, 48);
      if (!w || !h) { markProbed(0); return; }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { markProbed(0); return; }
      ctx.drawImage(probe, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      let opaque = 0;
      let light = 0;
      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        if (alpha < 40) continue; // ignore transparent pixels
        opaque += 1;
        const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        if (luminance >= 224) light += 1;
      }
      const isDark = opaque > 0 && light / opaque >= 0.92;
      markProbed(isDark);
      if (isDark) cb(true);
    } catch (e) {
      // Canvas tainted (logo host sent no CORS headers) - cannot inspect, leave as-is.
      markProbed(0);
    }
  };
  probe.src = url;
};

/**
 * Shared brand logo tile.
 * - Forces the known We Yan logo when the brand is We Yan.
 * - Falls through the provided `candidates` list on image load errors.
 * - Switches the tile to a dark background when the logo is detected as white.
 */
export const BrandLogo = ({
  name,
  candidates = [],
  storedLogo = '',
  initials,
  containerClassName = '',
  imgClassName = '',
  initialsClassName = '',
  // Optional: told which URL actually rendered, so a page can say where the
  // logo it is showing came from.
  onResolved,
}) => {
  const overrides = overrideCandidatesFor(name);
  const userCandidates = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
  // The logo on the brand record - uploaded, or scraped and saved - is an
  // explicit choice, so it outranks both the hardcoded name overrides and the
  // guessed favicons. Everything else keeps its existing order.
  const stored = Array.from(new Set(
    [storedLogo, ...userCandidates.filter(isInlineLogo)].filter(Boolean).map((c) => String(c).trim()),
  ));
  const guesses = userCandidates.filter((candidate) => !stored.includes(String(candidate).trim()));
  const overriddenCandidateKey = [...stored, ...overrides, ...guesses].join('|');
  const resolved = [...stored, ...overrides, ...guesses];
  // The cache is a fast path for GUESSING, so it only applies when the record
  // holds nothing. It is keyed by brand name, written on every successful load
  // and never expires, so consulting it ahead of the record's own logo pinned
  // an old favicon in place for good - a new upload could never displace it.
  //
  // It must also still be one of this brand's candidates. The cache outlives
  // the rules that produced its entries: a favicon guessed from a page the
  // brand was merely found on (a Pinterest pin) stayed pinned as that brand's
  // logo after such guesses were ruled out.
  const cachedEntry = stored.length ? '' : getCachedBrandLogo(name);
  const cached = cachedEntry && resolved.includes(cachedEntry) ? cachedEntry : '';
  // When there's a cached URL, try it first (fast path). When cache is empty
  // we must NOT prepend '' - an empty first entry short-circuits the <img>
  // render below and the whole fallback chain never fires.
  const initialResolved = cached
    ? [cached, ...resolved.filter((candidate) => candidate !== cached)]
    : resolved;
  const initialKey = initialResolved.join('|') || overriddenCandidateKey;
  const [index, setIndex] = useState(0);
  const [dark, setDark] = useState(false);
  const url = initialResolved[index] || '';

  useEffect(() => {
    setIndex(0);
    setDark(false);
  }, [initialKey, name]);

  useEffect(() => {
    if (!url) return undefined;
    let active = true;
    detectWhiteLogo(url, (isWhite) => {
      if (active && isWhite) setDark(true);
    });
    return () => { active = false; };
  }, [url]);

  return (
    <div
      className={containerClassName}
      style={dark ? { backgroundColor: '#1A1A1A', borderColor: '#1A1A1A' } : undefined}
    >
      {url ? (
        <img
          src={url}
          alt={`${name || 'Brand'} logo`}
          className={imgClassName}
          onLoad={() => {
            if (!stored.includes(url)) setCachedBrandLogo(name, url);
            if (onResolved) onResolved(url);
          }}
          onError={() => setIndex((current) => current + 1)}
        />
      ) : (
        <span className={initialsClassName} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          {initials || deriveInitials(name)}
        </span>
      )}
    </div>
  );
};

export default BrandLogo;
